import { useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import "./AppContracts.css";
import { Checkbox, FileText } from "../../../ui";
import DocumentCard from "../../components/DocumentCard/DocumentCard";
import DeleteSelectionToolbar from "../../components/DeleteSelectionToolbar/DeleteSelectionToolbar";
import useSelectionDelete from "../../hooks/useSelectionDelete";
import { APPS, APP_STATUS } from "../../utils/ludDocuments.constants";
import {
  formatDate,
  getLudFileName,
  openLudFile,
} from "../../utils/ludDocuments.helpers";
import { DocumentHistoryModal } from "../../../modals/DocumentHistory";
import { DeleteModal } from "../../../modals/Delete";
import PermanentDeleteModal from "../../components/PermanentDeleteModal/PermanentDeleteModal";
import {
  deleteLudContractAPI,
  permanentDeleteLudContractAPI,
  getLudContractHistoryAPI,
} from "../../../../utils/api/ludDocuments";

export default function AppContracts({
  restaurant,
  lang,
  token,
  refreshRestaurant,
  uploadTarget,
  onUploadTargetChange,
  canPermanentDelete = false,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const [historyTarget, setHistoryTarget] = useState(null);
  const normalDelete = useSelectionDelete({
    deleteOne: (id) => deleteLudContractAPI(token, restaurant.id, id),
    refresh: refreshRestaurant,
    lang,
  });
  const { can: canAccess } = useAccess();
  const permanentDelete = useSelectionDelete({
    // handler-level authorization, against this restaurant's public id
    deleteOne: (id) =>
      canAccess("ludDocument.permanentDelete", { restaurantId: restaurant.id })
        ? permanentDeleteLudContractAPI(token, restaurant.id, id)
        : Promise.reject(new Error("Not allowed")),
    refresh: refreshRestaurant,
    lang,
  });

  // The two selection modes are mutually exclusive.
  const startNormalDelete = () => {
    permanentDelete.cancel();
    normalDelete.start();
  };
  const startPermanentDelete = () => {
    normalDelete.cancel();
    permanentDelete.start();
  };
  const activeSelection = permanentDelete.active ? permanentDelete : normalDelete;

  const openContractHistory = async (event, key, contract) => {
    event.stopPropagation();

    try {
      const history = await getLudContractHistoryAPI(
        token,
        restaurant.id,
        contract.id,
      );

      setHistoryTarget({
        title: APPS[key].label[lang],
        history,
        kind: "contract",
        reference: contract.id,
        app: key,
      });
    } catch (error) {
      onError?.(
        error,
        ar ? "تعذّر تحميل سجل العقد" : "Could not load the contract history",
      );
    }
  };

  const hasCurrentContracts = Object.values(restaurant.contracts || {}).some(
    (contract) => contract?.currentFile,
  );
  const hasPermanentContracts = Object.values(restaurant.contracts || {}).some(
    (contract) => contract?.currentFile || contract?.history?.length,
  );

  return (
    <>
      {canManage &&
        (hasCurrentContracts ||
          (canPermanentDelete && hasPermanentContracts)) && (
          <div className="lud-delete-actions-row">
            {hasCurrentContracts ? (
              <DeleteSelectionToolbar
                selection={normalDelete}
                lang={lang}
                onStart={startNormalDelete}
              />
            ) : (
              <span />
            )}

            {canPermanentDelete && hasPermanentContracts ? (
              <DeleteSelectionToolbar
                selection={permanentDelete}
                lang={lang}
                permanent
                className="lud-delete-toolbar lud-delete-toolbar--permanent"
                onStart={startPermanentDelete}
              />
            ) : null}
          </div>
        )}

      <div className="lud-doc-grid">
        {restaurant.enabledApps.map((key) => {
          const contract = restaurant.contracts[key];
          const appStatus = APP_STATUS[restaurant.appStatus?.[key] || "active"];
          const hasCurrentFile = Boolean(contract?.currentFile);
          const isCancelled =
            contract?.status === "cancelled" || !hasCurrentFile;

          if (!contract) {
            return (
              <DocumentCard
                key={key}
                onClick={() => onUploadTargetChange?.({ app: key })}
                selected={uploadTarget?.app === key}
                title={APPS[key].label[lang]}
                subtitle={`${ar ? "لا يوجد عقد" : "No contract"} — ${appStatus.label[lang]}`}
                meta="—"
                status={appStatus.label[lang]}
                statusVariant={appStatus.variant}
                icon={<FileText size={21} />}
              />
            );
          }

          return (
            <DocumentCard
              key={key}
              onClick={() => onUploadTargetChange?.({ app: key })}
              selected={uploadTarget?.app === key}
              title={APPS[key].label[lang]}
              subtitle={
                hasCurrentFile
                  ? `${ar ? "تاريخ البدء" : "Start date"}: ${formatDate(contract.startDate, lang)}`
                  : `${ar ? "لا يوجد عقد حالي" : "No current contract"} — ${ar ? "النسخ السابقة في السجل" : "Previous versions are in history"}`
              }
              meta={
                hasCurrentFile ? (ar ? "عقد موقّع" : "Signed contract") : "—"
              }
              status={
                isCancelled
                  ? ar
                    ? "ملغى"
                    : "Cancelled"
                  : ar
                    ? "سارٍ"
                    : "Active"
              }
              statusVariant={isCancelled ? "danger" : "success"}
              icon={<FileText size={21} />}
              historyText={
                contract.history?.length || isCancelled ? (
                  <button
                    type="button"
                    className="lud-document-history-link"
                    onClick={(event) =>
                      openContractHistory(event, key, contract)
                    }
                  >
                    {ar
                      ? `يوجد ${contract.history?.length || 1} نسخة سابقة ملغاة — عرض السجل`
                      : `${contract.history?.length || 1} previous superseded versions — View history`}
                  </button>
                ) : (
                  ""
                )
              }
            >
              {(normalDelete.active || permanentDelete.active) &&
              (hasCurrentFile || contract.history?.length) ? (
                <div
                  className="lud-contract-delete-checkbox"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    checked={activeSelection.isSelected(contract.id)}
                    onChange={() => activeSelection.toggle(contract.id)}
                  />
                </div>
              ) : null}

              {hasCurrentFile ? (
                <button
                  type="button"
                  className="lud-contract-file-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    openLudFile(contract.currentFile, {
                      token,
                      storeId: restaurant.id,
                      kind: "contract",
                      reference: contract.id,
                      onError,
                    });
                  }}
                >
                  {getLudFileName({
                    file: { ...contract.currentFile, app: key },
                    kind: "contract",
                    reference: contract.id,
                    originalName: contract.currentFile.originalName,
                  })}
                </button>
              ) : null}
            </DocumentCard>
          );
        })}
      </div>

      <DocumentHistoryModal
        open={Boolean(historyTarget)}
        title={historyTarget?.title}
        history={(historyTarget?.history || []).map((entry) => {
          const historyFile = entry?.file || entry;
          const normalizedName = getLudFileName({
            file: { ...historyFile, app: historyTarget?.app },
            kind: "contract",
            reference: entry?.contractId || historyTarget?.reference,
            originalName: historyFile?.originalName,
          });
          return {
            ...entry,
            file: entry?.file
              ? { ...entry.file, originalName: normalizedName }
              : entry?.file,
            originalName: normalizedName,
          };
        })}
        lang={lang}
        onClose={() => setHistoryTarget(null)}
        onOpenFile={(entry) =>
          openLudFile(entry?.file || entry, {
            token,
            storeId: restaurant.id,
            kind: historyTarget?.kind,
            reference: entry?.contractId || historyTarget?.reference,
            historyId: entry?._id,
            onError,
          })
        }
      />

      <PermanentDeleteModal
        open={permanentDelete.confirmOpen}
        count={permanentDelete.selected.length}
        deleting={permanentDelete.deleting}
        error={permanentDelete.error}
        onConfirm={permanentDelete.confirm}
        onCancel={permanentDelete.closeConfirm}
      />

      {normalDelete.confirmOpen && (
        <DeleteModal
          id={null}
          loading={normalDelete.deleting}
          error={normalDelete.error}
          handleDelete={normalDelete.confirm}
          cancel={normalDelete.closeConfirm}
        />
      )}
    </>
  );
}
